import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { skills, skillCategories } from '../data/skills';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function SkillsSection() {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const skillCards = containerRef.current?.querySelectorAll('.skill-card');
      if (skillCards) {
        gsap.fromTo(
          skillCards,
          { opacity: 0, y: 40, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 75%',
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      id="skills"
      className="skills-section"
      style={{
        minHeight: '100vh',
        padding: '6rem 2rem',
        position: 'relative',
      }}
      aria-labelledby="skills-title"
    >
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 id="skills-title" className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, var(--fg-primary), var(--accent-tertiary))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t('skills.title')}
          </h2>
        </header>

        <div className="categories-nav" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginBottom: '3rem',
          flexWrap: 'wrap',
        }}>
          {skillCategories.map((cat) => (
            <button
              key={cat.id}
              className="category-btn"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--card-bg)',
                border: `1px solid ${cat.color}40`,
                borderRadius: '100px',
                color: cat.color,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={e => gsap.to(e.currentTarget, { background: `${cat.color}20`, scale: 1.05, duration: 0.2 })}
              onMouseLeave={e => gsap.to(e.currentTarget, { background: 'var(--card-bg)', scale: 1, duration: 0.2 })}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>

        <div className="skills-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}>
          {skills.map((skill) => {
            const category = skillCategories.find(c => c.id === skill.category);
            return (
              <article
                key={skill.id}
                className="skill-card"
                style={{
                  padding: '2rem',
                  background: 'var(--card-bg)',
                  border: `1px solid var(--border-color)`,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: '1rem',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  gsap.to(e.currentTarget, {
                    borderColor: category?.color || 'var(--accent-primary)',
                    boxShadow: `0 20px 40px ${category?.color || 'var(--accent-primary)'}20`,
                    y: -8,
                    duration: 0.3,
                  });
                }}
                onMouseLeave={e => {
                  gsap.to(e.currentTarget, {
                    borderColor: 'var(--border-color)',
                    boxShadow: 'none',
                    y: 0,
                    duration: 0.3,
                  });
                }}
              >
                <div
                  className="skill-icon-wrapper"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${category?.color}20, ${category?.color}05)`,
                    border: `1px solid ${category?.color}40`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                  }}
                >
                  {skill.icon}
                </div>
                <h3 className="skill-name" style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--fg-primary)',
                }}>
                  {t(skill.labelKey)}
                </h3>
                <span
                  className="skill-category-badge"
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: `${category?.color}20`,
                    border: `1px solid ${category?.color}40`,
                    borderRadius: '100px',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: category?.color,
                  }}
                >
                  {t(category?.labelKey || '')}
                </span>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}